import { BelayUserConnection, ErrorState, User } from './belay';
import { PageInfo } from './blazesPageInfo';
import { CommentSummary } from './comments';
import { LanguageRegionCode } from './LanguageRegionCode';
import LatLng from './LatLng';
import type { Obstacle } from './Obstacle';
import { ReactionSummary } from './reactions';
import type TrailInfo from './TrailInfo';
import type TrailRating from './TrailRating';
import type { Visibility } from './Visibility';

export type Location = LatLng & {
  city: string;
  city_id: number;
  country: number;
  country_name?: string;
  region: string;
  regionName: string;
};

export type Trail = {
  area: {
    id: number;
    name: string;
    slug: string;
  };
  name: string;
  slug: string;
  defaultActivityStats: {
    activity: string;
    difficulty: number;
    duration: number;
    rating: number;
    visitorUsage: number;
  };
  id: number;
  links: {
    href: string;
    rel: string;
  }[];
  location: Location;
  trailCounts: {
    photoCount: number;
    completedCount: number;
    reviewCount: number;
    trackCount: number;
  };
  trailGeoStats: {
    durationMinutes: string;
    length: number;
  };
  description?: string;
};

export type Activity = {
  activity: { uid: string; name: string };
  created_at: string;
  id: number;
  links?: {
    rel: string;
    href: string;
  }[];
  metadata: {
    created: string;
  };
  location: Location;
  name: string;
  photoCount: number;
  presentationType: string;
  rating: number;
  slug: string;
  summaryStats: null | {
    distanceTotal?: number;
    duration?: number;
    elevationGain?: number;
  };
  user: User;
};

export enum FeedName {
  FOLLOWERS = 'followers',
  PERSONAL = 'personal',
  PUBLIC = 'public',
  TIMELINE = 'timeline',
  LOCAL = 'local'
}

export type BlazesHrefSchema = {
  limit: {
    default: number;
    type: string;
  };
};

export enum FeedLinkRel {
  Next = 'next',
  Prev = 'prev',
  Start = 'start'
}

export type FeedLink = {
  rel: FeedLinkRel;
  href: string;
  hrefSchema?: BlazesHrefSchema;
  templateRequired?: string[];
};

export type BlazesFeedOption = {
  name: FeedName;
  links: FeedLink[];
  displayName: string;
};

export type FeedOptionLink = {
  [key: string]: {
    displayName: string;
    name: string;
    href: string;
  };
};

export type FeedOption = {
  displayName: string;
  name: FeedName;
  links: FeedOptionLink;
};

export type Review = {
  comment?: string;
  comment_lang?: LanguageRegionCode;
  comment_original?: string;
  created_at: string;
  id: number;
  rating: number;
  difficulty?: number;
  infoAttributes?: {
    uid: TrailInfo;
    name: string;
  }[];
  ratingAttributes?: {
    uid: TrailRating;
    name: string;
  }[];
  obstacles?: {
    uid: Obstacle;
    name: string;
  }[];
};

export enum FeedItemType {
  ActivityCreated = 'activity:created',
  ActivityPhotosUploaded = 'activity:photos:uploaded',
  ActivityPhotosUploadedV1 = 'activity:photos:uploaded:v1',
  TrailPhotosUploaded = 'trail:photos:uploaded',
  TrailPhotosUploadedV1 = 'trail:photos:uploaded:v1',
  TrailCompleted = 'trail:completed',
  ReviewsCreated = 'reviews:created',
  ListCreated = 'list:created',
  ListItemAdded = 'list:item:added',
  ListItemAddedV1 = 'list:item:added:v1'
}

export enum RecommendationType {
  FeaturedPhotos = 'featured-photos'
}

export enum FeedItemLinkRel {
  Delete = 'delete'
}

export type FeedItemLink = {
  rel: FeedItemLinkRel;
  href: string;
  method?: string;
};

export type DeletePostLink = {
  rel: FeedItemLinkRel.Delete;
  href: string;
  method: 'DELETE';
};

export type FeedItemData = {
  itemType: FeedItemType;
  itemID: string;
  commentSummary: CommentSummary;
  reactionSummary?: ReactionSummary;
  description: string;
  user: User;
  timestamp: string;
  timestampMessage?: string;
  connection?: BelayUserConnection;
  contentPrivacy?: Visibility;
  links?: FeedItemLink[];
};

export type FeaturedTrail = {
  id: number | string;
  name: string;
  slug: string;
};

export type PhotoUser = {
  id: number | string;
  firstName: string;
  lastName: string;
};

export type Photo = {
  id: number | string;
  imgUrl: string;
  created_at?: string;
  description?: string;
  caption?: string;
  user?: PhotoUser;
  photoScore?: string | number;
};

export type RecommendedContentSummary = {
  description: string;
  photos: Photo[];
  trail: FeaturedTrail;
};

export type PhotosItemData = FeedItemData & {
  activity?: Activity;
  trail?: Trail;
  photos: Photo[];
};

export type Addition = { additionType: 'trail'; additionData: Trail } | { additionType: 'activity'; additionData: Activity };

export type ListAdditionItemData = FeedItemData & {
  additions: Addition[];
  list: { id: number | string; name: string; slug: string };
  subtext?: string;
};

export type ReviewItemData = FeedItemData & {
  review: Review;
  trail: Trail;
};

export type ActivityCreatedItemData = FeedItemData & {
  activity: Activity;
  review?: Review;
  trail?: Trail;
};

export type TrailCompletedItemData = FeedItemData & {
  trail: Trail;
};

export type ItemData = ListAdditionItemData | PhotosItemData | ReviewItemData | ActivityCreatedItemData | TrailCompletedItemData;

export enum SectionType {
  FeedEmptyCta = 'feed-empty-cta',
  FeedItem = 'feed-item',
  FollowSuggestion = 'follow-suggestion',
  RecommendedContent = 'recommended-content',
  FeedHeader = 'feed-header',
  Shimmer = 'shimmer',
  ErrorState = 'error-state',
  FeedMetadata = 'feed-metadata'
}

export enum SuggestionType {
  SuggestedMembers = 'suggested-members',
  FriendsOnAllTrails = 'friends-on-alltrails'
}

export type FeedMetadata = {
  feedId: FeedName;
  placeResult?: {
    objectID: string;
  };
  prefixText?: string;
  displayName?: string;
  viewProperties?: {
    feedSearchLocation: string;
    feedSearchLocationId: string;
    feedSearchLocationType: string;
  };
  errorMessage?: string;
};

export type Section = {
  cursor?: string;
  section_type: SectionType;
  itemData?: ItemData;
  suggestions?: BelayUserConnection[];
  errorState?: ErrorState;
  trails?: RecommendedContentSummary[];
  dismissible?: boolean;
  sectionTitle?: string;
  suggestionType?: SuggestionType;
};

export type FeedPage = {
  sections: Section[];
  pageInfo: PageInfo;
  links: FeedLink[];
  metadata: FeedMetadata;
};
